// Stop microphone capture but keep the chat session alive (do not change isChatSessionActive).
// Important: do NOT close or touch the assistant playback AudioContext here.
export const stopMicCaptureKeepSession = (
  mediaRecorderRef: React.MutableRefObject<MediaRecorder | null>,
  mediaStreamRef: React.MutableRefObject<MediaStream | null>,
  addLog: (message: string, type?: "log" | "error" | "warn") => void,
  setIsRecording: (value: boolean) => void
) => {
  const recorder = mediaRecorderRef.current;
  if (recorder && recorder.state !== "inactive") {
    try {
      addLog("Stopping MediaRecorder due to mic toggle");
      recorder.stop();
      setIsRecording(false);
      return;
    } catch (e) {
      addLog(`Error stopping MediaRecorder from mic toggle: ${e instanceof Error ? e.message : String(e)}`, "error");
    }
  }

  if (mediaStreamRef.current) {
    addLog("Stopping media stream due to mic toggle");
    mediaStreamRef.current.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch (e) {
        addLog(`Error stopping media track from mic toggle: ${e instanceof Error ? e.message : String(e)}`, "warn");
      }
    });
    mediaStreamRef.current = null;
    setIsRecording(false);
  }
};

//function to encode a Float32Array to a WAV blob
export const encodeWavFromFloat32 = (samples: Float32Array, sampleRate: number): Blob => {
  const numChannels = 1;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const buffer = new ArrayBuffer(44 + samples.length * bytesPerSample);
  const view = new DataView(buffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + samples.length * bytesPerSample, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, samples.length * bytesPerSample, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += bytesPerSample;
  }

  return new Blob([buffer], { type: "audio/wav" });
};

// One-time microphone priming to satisfy mobile browser gesture requirements.
// Call this directly from the ChatBot button click so that later getUserMedia
// calls inside components are already authorised on iOS/Android browsers.
let micPrimed = false;

export const primeMicrophoneOnce = async () => {
  if (micPrimed) {
    return;
  }

  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return;
  }

  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return;
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    // Immediately stop tracks – we only need to obtain permission.
    stream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch {
        // ignore
      }
    });
    micPrimed = true;
  } catch {
    // If the user denies access here, later attempts will also fail; we just don't flip the flag.
    micPrimed = false;
  }
};

// Helper to send a single VAD segment (current PCM buffer) as one question turn
export const sendCurrentVadSegmentCore = async (
  isChatSessionActiveRef: React.MutableRefObject<boolean>,
  webAudioPcmDataRef: React.MutableRefObject<Float32Array[]>,
  webAudioSampleRateRef: React.MutableRefObject<number>,
  webAudioStatsRef: React.MutableRefObject<{ processCount: number; nonZeroSampleCount: number; maxSampleSeen: number }>,
  vadSpeechActiveRef: React.MutableRefObject<boolean>,
  vadSilenceMsRef: React.MutableRefObject<number>,
  vadSegmentMsRef: React.MutableRefObject<number>,
  isSendingSegmentRef: React.MutableRefObject<boolean>,
  addLog: (message: string, type?: "log" | "error" | "warn") => void,
  sendAudioToBackendAndStream: (wavBlob: Blob) => Promise<void>
) => {
  if (!isChatSessionActiveRef.current) {
    return;
  }

  const chunks = webAudioPcmDataRef.current;
  const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);

  if (totalLength === 0) {
    return;
  }

  if (isSendingSegmentRef.current) {
    return;
  }
  isSendingSegmentRef.current = true;

  try {
    const merged = new Float32Array(totalLength);
    let offset = 0;
    let maxAbsSample = 0;
    for (const chunk of chunks) {
      merged.set(chunk, offset);
      for (let i = 0; i < chunk.length; i++) {
        const v = Math.abs(chunk[i]);
        if (v > maxAbsSample) {
          maxAbsSample = v;
        }
      }
      offset += chunk.length;
    }

    addLog(`VAD segment: length=${merged.length}, maxAbsSample=${maxAbsSample.toFixed(4)}`);

    const wavBlob = encodeWavFromFloat32(merged, webAudioSampleRateRef.current || 44100);
    addLog(`VAD WAV blob created: size=${wavBlob.size} bytes, sampleRate=${webAudioSampleRateRef.current}, maxAbsSample=${maxAbsSample.toFixed(4)}`);

    // Reset buffer and stats for next question turn
    webAudioPcmDataRef.current = [];
    webAudioStatsRef.current = {
      processCount: 0,
      nonZeroSampleCount: 0,
      maxSampleSeen: 0,
    };
    vadSpeechActiveRef.current = false;
    vadSilenceMsRef.current = 0;
    vadSegmentMsRef.current = 0;

    // Send this question turn to backend (WS) and let audio/transcript stream back
    await sendAudioToBackendAndStream(wavBlob);
  } catch (err) {
    addLog(`Error while building/sending VAD segment: ${err instanceof Error ? err.message : String(err)}`, "error");
  } finally {
    isSendingSegmentRef.current = false;
  }
};

// Stop WebAudio recording and clean resources
export const stopWebAudioRecordingCore = async (
  audioContextRef: React.MutableRefObject<AudioContext | null>,
  scriptProcessorRef: React.MutableRefObject< ScriptProcessorNode | null>,
  sourceNodeRef: React.MutableRefObject<MediaStreamAudioSourceNode | null>,
  mediaStreamRef: React.MutableRefObject<MediaStream | null>,
  webAudioPcmDataRef: React.MutableRefObject<Float32Array[]>,
  webAudioStatsRef: React.MutableRefObject<{ processCount: number; nonZeroSampleCount: number; maxSampleSeen: number }>,
  vadSpeechActiveRef: React.MutableRefObject<boolean>,
  vadSilenceMsRef: React.MutableRefObject<number>,
  vadSegmentMsRef: React.MutableRefObject<number>,
  recordedAudioUrl: string | null,
  recordedWavUrl: string | null,
  setRecordedAudioUrl: (url: string | null) => void,
  setRecordedWavUrl: (url: string | null) => void,
  recordedWavBlobRef: React.MutableRefObject<Blob | null>,
  setIsRecording: (value: boolean) => void,
  addLog: (message: string, type?: "log" | "error" | "warn") => void,
  /** WebSocket ref for sending stop_transcription to backend (GCP Speech-to-Text). */
  chatSocketRef?: React.MutableRefObject<WebSocket | null>,
  /** Ref to interval ID for sending transcription chunks (to clear it on stop). */
  transcriptionIntervalRef?: React.MutableRefObject<NodeJS.Timeout | null>
) => {
  const audioContext = audioContextRef.current;
  const processor = scriptProcessorRef.current;
  const source = sourceNodeRef.current;

  addLog("Stopping WebAudio recorder");

  // GCP Speech-to-Text streaming: stop transcription and clear interval
  if (transcriptionIntervalRef && transcriptionIntervalRef.current) {
    clearInterval(transcriptionIntervalRef.current);
    transcriptionIntervalRef.current = null;
    addLog("[GCP Transcription] Interval cleared");
  }

  if (chatSocketRef && chatSocketRef.current) {
    const socket = chatSocketRef.current;
    if (socket.readyState === WebSocket.OPEN) {
      socket.send(
        JSON.stringify({
          type: 'stop_transcription',
        }),
      );
      addLog("[GCP Transcription] stop_transcription sent");
    }
  }

  // Log final stats
  const stats = webAudioStatsRef.current;
  addLog(`WebAudio final stats: ${stats.processCount} buffers processed, ${stats.nonZeroSampleCount} non-zero samples, maxAbs=${stats.maxSampleSeen.toFixed(4)}`);

  if (source && processor) {
    try {
      source.disconnect(processor);
      processor.disconnect();
    } catch (e) {
      addLog(`Error disconnecting WebAudio nodes: ${e instanceof Error ? e.message : String(e)}`, "warn");
    }
  }

  if (audioContext && audioContext.state !== "closed") {
    try {
      await audioContext.close();
    } catch (e) {
      addLog(`Error closing AudioContext: ${e instanceof Error ? e.message : String(e)}`, "warn");
    }
  }

  audioContextRef.current = null;
  scriptProcessorRef.current = null;
  sourceNodeRef.current = null;

  if (mediaStreamRef.current) {
    mediaStreamRef.current.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch (e) {
        addLog(`Error stopping media track for WebAudio: ${e instanceof Error ? e.message : String(e)}`, "warn");
      }
    });
    mediaStreamRef.current = null;
  }

  // At stop we do not send anything new; VAD segments are already sent while recording
  webAudioPcmDataRef.current = [];
  webAudioStatsRef.current = {
    processCount: 0,
    nonZeroSampleCount: 0,
    maxSampleSeen: 0,
  };
  vadSpeechActiveRef.current = false;
  vadSilenceMsRef.current = 0;
  vadSegmentMsRef.current = 0;

  if (recordedAudioUrl) {
    URL.revokeObjectURL(recordedAudioUrl);
  }
  if (recordedWavUrl) {
    URL.revokeObjectURL(recordedWavUrl);
  }

  setRecordedAudioUrl(null);
  setRecordedWavUrl(null);
  recordedWavBlobRef.current = null;

  setIsRecording(false);
};

// Start continuous hands-free recording with simple VAD to segment questions
export const startHandsFreeWebAudioSessionCore = async (
  recordedAudioUrl: string | null,
  recordedWavUrl: string | null,
  setRecordedAudioUrl: (url: string | null) => void,
  setRecordedWavUrl: (url: string | null) => void,
  setAudioLogs: (logs: string[]) => void,
  mediaStreamRef: React.MutableRefObject<MediaStream | null>,
  audioContextRef: React.MutableRefObject<AudioContext | null>,
  webAudioSampleRateRef: React.MutableRefObject<number>,
  sourceNodeRef: React.MutableRefObject<MediaStreamAudioSourceNode | null>,
  scriptProcessorRef: React.MutableRefObject<ScriptProcessorNode | null>,
  webAudioPcmDataRef: React.MutableRefObject<Float32Array[]>,
  webAudioStatsRef: React.MutableRefObject<{ processCount: number; nonZeroSampleCount: number; maxSampleSeen: number }>,
  vadSpeechActiveRef: React.MutableRefObject<boolean>,
  vadSilenceMsRef: React.MutableRefObject<number>,
  vadSegmentMsRef: React.MutableRefObject<number>,
  isChatSessionActiveRef: React.MutableRefObject<boolean>,
  isMicEnabledRef: React.MutableRefObject<boolean>,
  setIsRecording: (value: boolean) => void,
  setIsChatSessionActive: (value: boolean) => void,
  sendCurrentVadSegment: () => Promise<void>,
  addLog: (message: string, type?: "log" | "error" | "warn") => void,
  currentSpeakingForAuraRef?: React.MutableRefObject<boolean>,
  /** When false, VAD never auto-sends on silence; send only on user action (e.g. Send/Enter). */
  sendOnSilence: boolean = true,
  /** WebSocket ref for sending audio chunks to backend for live transcription (GCP Speech-to-Text). */
  chatSocketRef?: React.MutableRefObject<WebSocket | null>,
  /** When true, send PCM chunks to backend every ~250ms for live transcription. */
  sendTranscriptionChunks: boolean = false,
  /** Ref to store the interval ID for sending transcription chunks (so it can be cleared on stop). */
  transcriptionIntervalRef?: React.MutableRefObject<NodeJS.Timeout | null>,
  /** Called when start_transcription is sent so caller can reuse same params on reconnect. */
  onTranscriptionStarted?: (params: { sampleRateHertz: number; languageCode: string }) => void
) => {
  // Basic feature detection
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    console.warn("Recording is not available in this environment.");
    return;
  }

  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    console.warn("getUserMedia is not supported in this browser.");
    return;
  }

  const AudioContextCtor = (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!AudioContextCtor) {
    console.warn("WebAudio AudioContext is not available in this browser.");
    return;
  }

  try {
    addLog("Starting hands-free WebAudio session (VAD)...");

    // Clean previous URLs
    if (recordedAudioUrl) {
      URL.revokeObjectURL(recordedAudioUrl);
      setRecordedAudioUrl(null);
    }
    if (recordedWavUrl) {
      URL.revokeObjectURL(recordedWavUrl);
      setRecordedWavUrl(null);
    }

    setAudioLogs([]);

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    mediaStreamRef.current = stream;

    const audioContext: AudioContext = new AudioContextCtor();
    audioContextRef.current = audioContext;
    // iOS Safari: AudioContext starts suspended; resume inside user gesture
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }
    webAudioSampleRateRef.current = audioContext.sampleRate;

    const source = audioContext.createMediaStreamSource(stream);
    sourceNodeRef.current = source;

    const bufferSize = 4096;

    webAudioPcmDataRef.current = [];
    webAudioStatsRef.current = {
      processCount: 0,
      nonZeroSampleCount: 0,
      maxSampleSeen: 0,
    };
    vadSpeechActiveRef.current = false;
    vadSilenceMsRef.current = 0;
    vadSegmentMsRef.current = 0;
    if (currentSpeakingForAuraRef) {
      currentSpeakingForAuraRef.current = false;
    }

    // VAD parameters (tuned conservatively)
    const silenceThreshold = 0.022; // higher = only louder sound counts as speech
    const auraThreshold = 0.018; // slightly lower for responsive aura expand/retract
    const minSegmentMs = 550; // minimal speech duration (ms) to consider a real question
    const silenceToEndMs = 2000; // 2 seconds required trailing silence to close the segment
    const bufferDurationMs = (bufferSize / audioContext.sampleRate) * 1000;

    // Shared handler: processes a Float32Array buffer from either AudioWorklet or ScriptProcessor
    const processAudioBuffer = (samplesCopy: Float32Array): void => {
      if (!isChatSessionActiveRef.current || !isMicEnabledRef.current) {
        return;
      }

      webAudioPcmDataRef.current.push(samplesCopy);

      let nonZero = 0;
      let maxAbs = 0;
      let sumSquares = 0;
      for (let i = 0; i < samplesCopy.length; i++) {
        const v = samplesCopy[i];
        if (v !== 0) {
          nonZero++;
        }
        const av = Math.abs(v);
        if (av > maxAbs) {
          maxAbs = av;
        }
        sumSquares += v * v;
      }

      const stats = webAudioStatsRef.current;
      stats.processCount += 1;
      stats.nonZeroSampleCount += nonZero;
      if (maxAbs > stats.maxSampleSeen) {
        stats.maxSampleSeen = maxAbs;
      }

      const rms = Math.sqrt(sumSquares / (samplesCopy.length || 1));

      // Aura UI: per-buffer speaking flag so aura retracts as soon as user stops
      if (currentSpeakingForAuraRef) {
        currentSpeakingForAuraRef.current = rms > auraThreshold;
      }

      // Simple RMS-based VAD
      if (rms > silenceThreshold) {
        vadSpeechActiveRef.current = true;
        vadSilenceMsRef.current = 0;
        vadSegmentMsRef.current += bufferDurationMs;
      } else {
        if (vadSpeechActiveRef.current) {
          vadSilenceMsRef.current += bufferDurationMs;
          vadSegmentMsRef.current += bufferDurationMs;

          if (
            sendOnSilence &&
            vadSilenceMsRef.current >= silenceToEndMs &&
            vadSegmentMsRef.current >= minSegmentMs
          ) {
            // We detected end of a speech segment (question). Send it as one turn.
            void sendCurrentVadSegment();
          }
        }
      }
    };

    // AudioWorklet (modern, reliable on iOS) with ScriptProcessor fallback
    let usingWorklet = false;
    if (audioContext.audioWorklet) {
      try {
        // Inline worklet: accumulates 4096 samples then posts Float32Array to main thread
        const workletSrc = [
          'class P extends AudioWorkletProcessor{',
          '  constructor(){super();this.b=new Float32Array(4096);this.i=0}',
          '  process(inputs){',
          '    const ch=inputs[0]&&inputs[0][0];if(!ch)return true;',
          '    for(let j=0;j<ch.length;j++){',
          '      this.b[this.i++]=ch[j];',
          '      if(this.i>=4096){this.port.postMessage({d:new Float32Array(this.b)});this.i=0}',
          '    }return true}',
          '}registerProcessor("mic-cap",P);',
        ].join('\n');
        const blob = new Blob([workletSrc], { type: 'application/javascript' });
        const blobUrl = URL.createObjectURL(blob);
        await audioContext.audioWorklet.addModule(blobUrl);
        URL.revokeObjectURL(blobUrl);

        const workletNode = new AudioWorkletNode(audioContext, 'mic-cap');
        workletNode.port.onmessage = (ev: MessageEvent) => {
          const samples = ev.data?.d as Float32Array | undefined;
          if (samples) processAudioBuffer(samples);
        };
        source.connect(workletNode);
        workletNode.connect(audioContext.destination);
        usingWorklet = true;
        addLog(`AudioWorklet capture active (sampleRate=${audioContext.sampleRate})`);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        addLog(`AudioWorklet failed, using ScriptProcessor fallback: ${msg}`, "warn");
      }
    }

    if (!usingWorklet) {
      // Deprecated ScriptProcessor fallback for browsers without AudioWorklet
      const processor = audioContext.createScriptProcessor(bufferSize, 1, 1);
      scriptProcessorRef.current = processor;

      processor.onaudioprocess = (event: AudioProcessingEvent) => {
        const input = event.inputBuffer.getChannelData(0);
        const samplesCopy = new Float32Array(input.length);
        samplesCopy.set(input);
        processAudioBuffer(samplesCopy);
      };

      source.connect(processor);
      processor.connect(audioContext.destination);
      addLog(`ScriptProcessor capture active (sampleRate=${audioContext.sampleRate})`);
    }

    // GCP Speech-to-Text streaming: start transcription and send chunks every ~250ms
    if (sendTranscriptionChunks && chatSocketRef && chatSocketRef.current) {
      const socket = chatSocketRef.current;
      if (socket.readyState === WebSocket.OPEN) {
        // Detect user language from browser locale for transcription
        const browserLang = (typeof navigator !== 'undefined' && navigator.language)
          ? navigator.language.toLowerCase()
          : 'en';
        const gcpLanguageCode =
          browserLang.startsWith('es') ? 'es-419'   // Spanish (Latin America); use 'es-ES' for Spain
          : browserLang.startsWith('pt') ? 'pt-BR'
          : browserLang.startsWith('fr') ? 'fr-FR'
          : browserLang.startsWith('en') ? 'en-US'
          : 'en-US'; // fallback

        // Send start_transcription message to backend
        const sampleRateHertz = audioContext.sampleRate;
        socket.send(
          JSON.stringify({
            type: 'start_transcription',
            sampleRateHertz,
            languageCode: gcpLanguageCode,
          }),
        );
        addLog(`[GCP Transcription] start_transcription sent (sampleRate=${sampleRateHertz})`);
        onTranscriptionStarted?.({ sampleRateHertz, languageCode: gcpLanguageCode });

        // Start interval to send PCM chunks every 100ms (faster = more responsive)
        let lastSeenSocket: WebSocket | null = socket;
        const intervalId = setInterval(() => {
          if (!isChatSessionActiveRef.current || !isMicEnabledRef.current) {
            return;
          }
          // Detect socket swap (reconnect) — log once per change for diagnostics
          const currentWs = chatSocketRef?.current ?? null;
          if (currentWs !== lastSeenSocket) {
            console.log(`[GCP Transcription] Socket changed in interval: was=${lastSeenSocket ? 'WS' : 'null'}, now=${currentWs ? 'WS' : 'null'}, readyState=${currentWs?.readyState}`);
            lastSeenSocket = currentWs;
          }

          const chunks = webAudioPcmDataRef.current;
          if (chunks.length === 0) {
            return;
          }

          // Merge all accumulated PCM chunks into a single Float32Array
          const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
          
          // Skip if too little data (less than 0.05s of audio at 48kHz = 2400 samples)
          if (totalLength < 2400) {
            return;
          }

          const merged = new Float32Array(totalLength);
          let offset = 0;
          for (const chunk of chunks) {
            merged.set(chunk, offset);
            offset += chunk.length;
          }

          // Clear buffer (but keep VAD stats for silence detection)
          webAudioPcmDataRef.current = [];

          // Convert Float32 [-1, 1] to Int16 (LINEAR16 for GCP)
          const int16Buffer = new Int16Array(merged.length);
          for (let i = 0; i < merged.length; i++) {
            const s = Math.max(-1, Math.min(1, merged[i]));
            int16Buffer[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
          }

          // Convert Int16Array to Uint8Array (browser-compatible)
          const uint8Array = new Uint8Array(int16Buffer.buffer);
          
          // Convert Uint8Array to base64 (browser-compatible)
          let binaryString = '';
          for (let i = 0; i < uint8Array.length; i++) {
            binaryString += String.fromCharCode(uint8Array[i]);
          }
          const base64Chunk = btoa(binaryString);

          // Send to backend; use ref so after reconnect chunks go to the new socket
          const ws = chatSocketRef.current;
          if (ws?.readyState === WebSocket.OPEN) {
            ws.send(
              JSON.stringify({
                type: 'audio_chunk_for_transcript',
                payload: base64Chunk,
              }),
            );
          }
        }, 100); // send every 100ms for faster response

        if (transcriptionIntervalRef) {
          transcriptionIntervalRef.current = intervalId;
        }
      }
    }

    setIsRecording(true);
    setIsChatSessionActive(true);
    isChatSessionActiveRef.current = true;

    addLog(`Hands-free WebAudio session started. sampleRate=${audioContext.sampleRate}, bufferSize=${bufferSize}`);
  } catch (error) {
    addLog(`Error starting hands-free WebAudio session: ${error instanceof Error ? error.message : String(error)}`, "error");
    // Rethrow so caller can avoid showing mic aura when permission was denied
    throw error;
  }
};
